
import type React from "react"
import { useState, useEffect, useRef } from "react"
import { X } from "lucide-react"
import { motion, AnimatePresence } from "framer-motion"
import "@components/react/pricing/css/mkto_step_form.css"

interface Step {
  title: string
  description?: string
}

interface AppleMultiStepModalProps {
  isOpen: boolean
  onClose: () => void
  title: string
  steps: Step[]
  //children: React.ReactNode[]
  showSuccessState?: boolean
  successTitle?: string
  successMessage?: string
  onComplete?: () => void
  selectedPlan?: string
  volumePricingMessage?: string
  currentSite: Page.Site
  invalidDomains: Array<string>
}

function validateEmailFormat(email: string) {
  var re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(String(email).toLowerCase());
}

function isEmailBlacklisted(invalidDomains: string[], email: string): boolean {
  const lowerCaseInvalidDomains = invalidDomains.map(domain => domain.toLowerCase());
  const parts = email.toLowerCase().split('@');
  if (parts.length < 2) return false; // invalid email format
  const domain = parts[1]; // e.g., "gmail.com"
  const company = domain.split('.')[0]; // e.g., "gmail"
  return (
    lowerCaseInvalidDomains.includes(domain) ||
    lowerCaseInvalidDomains.includes(company)
  );
}

function getUrlSearchParams(url: string) {
  const _url = new URL(url);
  const defaultParams = {
    utm_source: '(direct)',
    utm_medium: '(none)',
    utm_campaign: '(not set)',
    utm_content: '',
    utm_term: '(not provided)'
  };
  return Object.fromEntries(
    Object.entries(defaultParams).map(([key, defaultValue]) => {
      const paramValue = _url.searchParams.get(key);
      return [key, paramValue !== null ? encodeURIComponent(paramValue) : defaultValue];
    })
  );
}

export function AppleMultiStepModal({
  isOpen,
  onClose,
  currentSite,
  invalidDomains
}: AppleMultiStepModalProps) {
  const [currentStep, setCurrentStep] = useState(0)
  const [isClosing, setIsClosing] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const modalRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    localStorage.removeItem('formSubmitted');
  }, []);

  //Marketo Form Steps
  useEffect(() => {
    if (!isOpen) return;

    const userConfig:{
      buttons: {
        prev: {
          label: string;
          disabled: boolean;
        };
        next: {
          label: string;
        };
      };
      requiredFields: {
        name: string;
        message: string;
        label: HTMLLabelElement | null;
        refEl: HTMLFormElement | null;
      }[];
    } = {
      buttons: {
        prev: { label: "Back", disabled: false },
        next: { label: "Next" }
      },
      requiredFields: [
        {
          name: "FirstName",
          message: "This field is required.",
          label: null,
          refEl: null
        },
        {
          name: "LastName",
          message: "This field is required.",
          label: null,
          refEl: null
        },
        {
          name: "Email",
          message: "Please enter your business email.",
          label: null,
          refEl: null
        },
        {
          name: "Company",
          message: "This field is required.",
          label: null,
          refEl: null
        },
        {
          name: "new_jobtitle",
          message: "This field is required.",
          label: null,
          refEl: null
        },
        {
          name: "NumberOfEmployees",
          message: "This field is required.",
          label: null,
          refEl: null
        },
        {
          name: "new_country",
          message: "This field is required.",
          label: null,
          refEl: null
        },
        {
          name: "new_gdprconsent",
          message: "This field is required.",
          label: null,
          refEl: null
        }
      ]
    }

    //Marketo Form Steps
    function setupMultistepLogic(form: FormObject) {

      var formEl = form.getFormElem()[0] as HTMLFormElement;
      if (!formEl) {
        console.warn("form.getFormElem() returned no element");
        return;
      }

      //var arrayify = getSelection.call.bind([].slice);

      var fieldRowStor = ".mktoForm > .mktoFormRow",
        buttonRowStor = ".mktoForm > .mktoButtonRow",
        buttonStor = ".mktoButtonRow .mktoButton",
        fsaatPrefix = "fsaat-",
        localFragmentAttr = "data-form-local-fragment";

      var CSSOM_RULEPOS_FIRST = 0;

      var fieldRows = formEl.querySelectorAll(fieldRowStor) as NodeListOf<HTMLDivElement>,
        submitButtonRow = formEl.querySelector(buttonRowStor) as HTMLDivElement,
        submitButton = submitButtonRow.querySelector(buttonStor) as HTMLButtonElement;

      userConfig.requiredFields
        .map(function (fieldDesc) {
          fieldDesc.label = formEl.querySelector("[for='" + fieldDesc.name + "']");
          fieldDesc.refEl = formEl.querySelector("[name='" + fieldDesc.name + "']");
          return fieldDesc;
        })
        .forEach(function (fieldDesc) {
          if (fieldDesc.label && fieldDesc.label.parentNode) {
            const parentElement = fieldDesc.label.parentNode as HTMLElement;
            parentElement.classList.add("mktoRequiredField");
          }
        });

      const dynableSheet = Array.from(document.styleSheets).find(sheet => {
        return sheet.ownerNode?.nodeName === "STYLE";
      });

      //console.log(dynableSheet);

      Array.from(fieldRows).forEach(function (row:any, rowIdx:any) {
        var rowPos = {
          isFirst: rowIdx == 0,
          isLast: rowIdx == fieldRows.length - 1
        };

        // id each wrapper row in DOM order
        row.id = fsaatPrefix + rowIdx;

        interface NavButtons {
          next?: HTMLButtonElement;
          prev?: HTMLButtonElement;
        }
        var navButtonRow = rowPos.isLast
            ? submitButtonRow
            : submitButtonRow.cloneNode(true),
          newRowAxis = row.nextSibling,
          nextEnabled = !rowPos.isLast,
          prevEnabled = !rowPos.isFirst && !userConfig.buttons.prev.disabled;

        let newButtonAxis: Node | null = null;
        let newButtonTmpl: HTMLButtonElement | null = null;
        const navButtons: NavButtons = {};

        if (nextEnabled) {
            const nextButton = (navButtonRow as Element).querySelector<HTMLButtonElement>(buttonStor);
            if (nextButton) {
              navButtons.next = nextButton;
            }
          }

        if (prevEnabled) {
          newButtonTmpl = newButtonAxis = navButtons.next || submitButton;
          navButtons.prev = newButtonTmpl.cloneNode() as HTMLButtonElement;
          newButtonAxis = navButtons.next || submitButton;
        }

        (Object.keys(navButtons) as Array<keyof NavButtons>).forEach(function (dir) {
          const btn = navButtons[dir];
          if (btn) {
            btn.type = "button";
            btn.setAttribute("data-dir", dir);
            btn.innerHTML = userConfig.buttons[dir].label;
          }
        });

        if (nextEnabled) {
          row.parentNode.insertBefore(navButtonRow, newRowAxis);
        }

        if (prevEnabled) {
          const prevBtn = navButtons.prev;
          if (prevBtn && newButtonAxis && newButtonAxis.parentNode) {
            newButtonAxis.parentNode.insertBefore(prevBtn, newButtonAxis);
          }
        }
        navButtonRow.addEventListener("click", function (e:Event) {
          const target = e.target as HTMLElement;
          if (target.tagName === "BUTTON" && (target as HTMLButtonElement).type === "button") {
            const dirAttr = target.getAttribute("data-dir");
            if (dirAttr === "next" && !isCustomValid(true, row)) {
              return;
            }

            if (dirAttr === "next" || dirAttr === "prev") {
              fsaatSet(row, dirAttr);
            } else {
              fsaatSet(row);
            }
          }
        });

        if (dynableSheet) {
          let selector;
          if (row.id === "fsaat-2") {
            // 特殊情况：让按钮行紧挨着 captcha
            selector = [
              `.mktoForm[${localFragmentAttr}="#${row.id}"] .mktoFormRow#${row.id},`,
              `.mktoForm[${localFragmentAttr}="#${row.id}"] .mktoCaptchaDisclaimer + .mktoButtonRow`
            ].join(" ");
          } else {
            // 默认情况：按钮行紧挨着当前行
            selector = [
              `.mktoForm[${localFragmentAttr}="#${row.id}"] .mktoFormRow#${row.id},`,
              `.mktoForm[${localFragmentAttr}="#${row.id}"] .mktoFormRow#${row.id} + .mktoButtonRow`
            ].join(" ");
          }

          const rule = `${selector} { display: block; }`;

          dynableSheet.insertRule(rule, CSSOM_RULEPOS_FIRST);
        }
      });

      const STEP_TITLES = [
        { title: "Your Information", description: "Tell us about yourself" },
        { title: "Company Details", description: "Tell us about your organization" },
        { title: "Review", description: "Confirm your information" }
      ];

      function fsaatSet(current: number | HTMLElement = 0, dir?: "prev" | "next") {
        const FSAAT_DIR_PREV = "prev";
        const FSAAT_DIR_NEXT = "next";

        let currentIndex = -1;

        if (current instanceof HTMLElement) {
          const idPart = current.id.split(fsaatPrefix)[1];
          currentIndex = parseInt(idPart, 10);
        } else if (typeof current === "number" && !isNaN(current)) {
          currentIndex = current;
        }

        let newIndex = currentIndex;

        if (dir === FSAAT_DIR_NEXT) {
          newIndex = currentIndex + 1;
        } else if (dir === FSAAT_DIR_PREV) {
          newIndex = currentIndex - 1;
        }
        // 如果 dir 是 undefined，就跳到 currentIndex（不变）

        const newHash = "#" + fsaatPrefix + newIndex;

        updateProgress(newIndex);
        formEl.setAttribute(localFragmentAttr, newHash);

        if (newHash === "#fsaat-2") {
          showConfirmationData();
        }
      }

      function updateProgress(currentStep: number) {
        setCurrentStep(currentStep);
        const stepContainers = document.querySelectorAll('.step-container');
        const stepTitle = document.querySelector('.step-title');
        const pricingMessageContainer = document.querySelector('.mkto-pricing-message');
        stepContainers.forEach((container, index) => {
          const circle = container.querySelector('.step-circle');
          const connector = container.querySelector('.step-connector');
          if (index < currentStep && circle) {
            // 已完成Step
            circle.classList.remove('bg-gray-100', 'text-gray-400');
            circle.classList.add('bg-green-500', 'text-white');
            circle.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-check h-3 w-3 sm:h-4 sm:w-4"><path d="M20 6 9 17l-5-5"></path></svg>`;
            if (connector) {
              connector.classList.remove('bg-gray-200');
              connector.classList.add('bg-green-500');
            }
          }
          else if (index === currentStep && circle) {
            // 当前Step
            circle.classList.remove('bg-gray-100', 'text-gray-400');
            circle.classList.add('bg-[#3037e2]', 'text-white');
            if (connector) {
              connector.classList.remove('bg-green-500');
              connector.classList.add('bg-gray-200');
            }
          }
          else {
            // 未完成Step
            if (circle){
              circle.classList.remove('bg-green-500', 'bg-[#3037e2]', 'text-white');
              circle.classList.add('bg-gray-100', 'text-gray-400');
              circle.textContent = (index + 1).toString();
            }
            if (connector) {
              connector.classList.remove('bg-green-500');
              connector.classList.add('bg-gray-200');
            }
          }
        });
        // 更新Step标题
        if (stepTitle && STEP_TITLES[currentStep]) {
          stepTitle.innerHTML = `
            <h3 class="text-base font-medium text-gray-900">${STEP_TITLES[currentStep].title}</h3>
            <p class="text-sm text-gray-500">${STEP_TITLES[currentStep].description}</p>
          `;
        }
      }

      function showConfirmationData() {
        const values = form.getValues();
        const spans = formEl.querySelectorAll("[data-confirm]");
        spans.forEach((span: any) => {
          const fieldName = span.getAttribute("data-confirm");
          const value = values[fieldName] || "";
          span.textContent = value;

          if (fieldName === "new_stateprovinceforuscaau") {
            const wrapper = span.closest(".confirmation-row") || span.parentElement;
            if (wrapper) {
              wrapper.style.display = value ? "block" : "none";
            }
          }
        });
      }

      document.querySelectorAll('.step-container').forEach(container => {
        container.addEventListener('click', () => {
          const stepIndex = parseInt(container.getAttribute('data-step') || '0', 10);
          if (stepIndex < currentStep) {
            fsaatSet(stepIndex); // 精确跳转
          }
        });
      });

      updateProgress(0);

      function isCustomValid(native:boolean, currentStep:any) {
        if(!native) return true;

        var currentStep = currentStep || formEl;
        var currentValues = form.getValues();
        var neverbounce = "unknown";
        var currentUnfilled = userConfig.requiredFields.filter(function (fieldDesc) {
          if(fieldDesc.name === "Email")neverbounce = fieldDesc.refEl?.dataset?.ziNeverbounceStatus?.trim().toLowerCase() ?? "unknown";

          return (
            fieldDesc.refEl && currentStep.contains(fieldDesc.refEl) &&
            (
              !currentValues[fieldDesc.name] || (
                fieldDesc.name === "Email" && (
                  !validateEmailFormat(currentValues["Email"]) ||
                  isEmailBlacklisted(invalidDomains , currentValues["Email"]) ||
                  ["invalid", "disposable"].includes(neverbounce)
                )
              ) || (
                fieldDesc.refEl.type === "checkbox" && currentValues[fieldDesc.name] === "no"
              )
            )
          );
        });
        if (currentUnfilled.length) {
          form.showErrorMessage(
            currentUnfilled[0].message,
            (window as any).MktoForms2.$(currentUnfilled[0].refEl)
          );
          return false;
        } else {
          return true;
        }
      }

      fsaatSet();

    }

    const interval = setInterval(() => {
      if (window.MktoForms2) {
        clearInterval(interval);
        const formEl = document.getElementById("marketoForm");
        const progressEl = document.getElementById("form-progress");
        const thankYouDiv = document.querySelector('.form-thanks');
        const hasSubmitted = localStorage.getItem('formSubmitted') === 'true';
        if (hasSubmitted) {
          if (formEl) formEl.style.display = 'none';
          if (progressEl) progressEl.style.display = 'none';
          if (thankYouDiv) thankYouDiv.classList.remove('hidden');
          return;
        }
        window.MktoForms2.loadForm("https://pages.avepoint.com", "853-GWT-606", 26419, (form) => {
          if (formEl) {
            form.getFormElem().appendTo(formEl);
            //hidden fields
            let hiddenFieldObject = {
              utm_source: "",
              utm_medium: "",
              utm_campaign: "",
              utm_content: "",
              utm_term: "",
              gclid: "",
              new_msclkid: "",
              new_campaignmemberstatus: "Registered",
              globalOptin: form.vals().globalOptin ? form.vals().globalOptin : "yes",
              campaignCode: "CMP-14256-N9Z5F",
              segmentTrackType: 'landing page',
              new_istestdata: window.location.hostname !== "www.avepoint.com"
            };
            if (!!getUrlSearchParams(currentSite?.href)) {
              Object.assign(hiddenFieldObject, getUrlSearchParams(currentSite?.href));
            }
            form.addHiddenFields(hiddenFieldObject);
            // end
            form.onSubmit(function() {
              var vals = form.vals();
              // updated utm fields value
              var utmObject = window.hasOwnProperty("getUTMHiddenFieldsValue") ? window.getUTMHiddenFieldsValue() : null;
              if(!!utmObject) form.vals(utmObject);
            });
            form.onSuccess(function(values, followUpUrl) {
              localStorage.setItem('formSubmitted', 'true');
              formEl.style.display = 'none';
              if (progressEl) progressEl.style.display = 'none';
              if (thankYouDiv) thankYouDiv.classList.remove("hidden");
              return false;
            });
          }
          setupMultistepLogic(form);
        });
      }
    }, 300);

    return () => {
      clearInterval(interval);
    };
  }, [isOpen]);

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        handleClose()
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside)
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [isOpen])

  // Handle escape key to close
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        handleClose()
      }
    }

    if (isOpen) {
      document.addEventListener("keydown", handleEscKey)
    }

    return () => {
      document.removeEventListener("keydown", handleEscKey)
    }
  }, [isOpen])

  // Add this effect to manage body scroll locking
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add("modal-open")
    } else {
      document.body.classList.remove("modal-open")
    }

    return () => {
      document.body.classList.remove("modal-open")
    }
  }, [isOpen])

  // Smooth closing animation
  const handleClose = () => {
    setIsClosing(true)
    setTimeout(() => {
      setIsClosing(false)
      onClose()
    }, 100)
  }

  if (!isOpen) return null

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop with blur effect */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="absolute inset-0 bg-black/30 backdrop-blur-sm"
            onClick={handleClose}
            aria-hidden="true"
          />

          {/* Modal container */}
          <motion.div
            ref={modalRef}
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{
              type: "spring",
              damping: 25,
              stiffness: 300,
              duration: 0.3,
            }}
            className="relative w-full max-w-lg mx-4 bg-white rounded-2xl shadow-2xl p-0 max-h-[95vh] overflow-y-auto popup-step-from"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button - floating style */}
            <button
              onClick={handleClose}
              className="absolute top-2.5 right-2.5 z-10 flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-gray-200"
              aria-label="Close modal"
            >
              <X className="h-4 w-4" />
            </button>

            {/* Content container with subtle shadow for scrollable area */}
            <div className="flex flex-col h-full">
              {/* Header with gradient background */}
              <div className="px-6 sm:px-8 pt-3 sm:pt-6 pb-2 sm:pb-4 bg-gradient-to-r from-gray-50 to-white border-b">
                <h2 className="text-2xl font-bold text-gray-900 tracking-tight">Request a Custom Quote</h2>
                <div className="mt-5" id="form-progress">
                  <div className="flex items-center space-x-1 sm:space-x-2 mb-2">
                    <div className="flex items-center step-container" data-step="0">
                      <div className="step-circle bg-gray-100 text-gray-400">1</div>
                      <div className="step-connector bg-gray-200"></div>
                    </div>
                    <div className="flex items-center step-container" data-step="1">
                      <div className="step-circle bg-gray-100 text-gray-400">2</div>
                      <div className="step-connector bg-gray-200"></div>
                    </div>
                    <div className="flex items-center step-container" data-step="2">
                      <div className="step-circle bg-gray-100 text-gray-400">3</div>
                    </div>
                  </div>
                  <div className="mb-2 step-title">
                    <h3 className="text-base font-medium text-gray-900">Your Information</h3>
                    <p className="text-sm text-gray-500">Tell us about yourself</p>
                  </div>
                </div>
              </div>

              <div id="marketoForm" className="custom-scrollbar step-form min-h-[340px]"></div>

              <div className="hidden py-15 px-10 form-thanks text-center space-y-4" >
                <img src="https://assets.avepoint.com/upload/images/solutions/images/icons/icon-Ok.svg" width={50} height={50} alt="right" className="mx-auto mb-6" />
                <h2>Thank you!</h2>
                <p>Your request has been submitted. Our team will contact you shortly.</p>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  )
}
